import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, Download, FileSpreadsheet, Clock } from "lucide-react";

import Button from "@/components/ui/Button";
import Layout from "@/components/layout/Layout";
import Spinner from "@/components/ui/Spinner";
import useGlobalStore from "@/store/globalStore";
import {
  RetentionEventStatus,
  RetentionMediaEntry,
  RetentionStatusError,
  downloadRetentionArtifact,
  downloadRetentionMedia,
  fetchRetentionEventStatus,
} from "@/api/retention";

/**
 * The Protected_Download page every retention email links to
 * (`/retention/events/:eventId/downloads`).
 *
 * Deleting a campaign, flight, asset, or asset version starts a 30-day retention window:
 * the records are soft-deleted, an export workbook is generated, and the media the scope
 * referenced is kept in private storage until the deadline. The email tells the
 * Responsible_User that; this page is where they act on it.
 *
 * ### Why the downloads are not links
 *
 * The backend mints no signed or public storage URL for retention data — it streams the
 * bytes through the application so entitlement is re-evaluated on every single request,
 * which is what makes access stop at the deadline or on restoration. A browser following
 * a bare `<a href>` would send no bearer token and get a 401, so each download is an
 * authenticated blob fetch (see `@/api/retention`). The email's links point *here*, and
 * this page holds the only buttons that work.
 *
 * ### Deep links from the email
 *
 * The per-file links narrow to one item with `?artifact=` or `?media=`. Those are honoured
 * by auto-starting that one download once the status loads, so a recipient who clicked
 * "LAlala_English_Instagram-Story_approved.png" gets that file rather than a list to
 * search through. The parameter is consumed once — a re-render must not re-trigger a
 * download, and neither must a manual refresh after the auto-download already ran.
 *
 * ### Expiry is information, not an error
 *
 * `downloads_available` folds in both the deadline and any restoration, so the page gates
 * on that one flag rather than recomputing the rule. A closed window renders as a plain
 * explanation: the entitlement legitimately ended, and the API deliberately answers a
 * denial, an unknown event, and another agency's event identically, so there is nothing
 * more specific to say without leaking whether the event exists.
 */
export default function RetentionDownloadsPage() {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const setError = useGlobalStore((state) => state.setError);

  const [status, setStatus] = useState<RetentionEventStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<RetentionStatusError | null>(null);
  /** Ids currently downloading, so one button can spin without freezing the others. */
  const [busyIds, setBusyIds] = useState<string[]>([]);
  /** Deep-link targets already acted on, so a re-render cannot re-download. */
  const [consumedDeepLink, setConsumedDeepLink] = useState(false);

  const requestedArtifactId = searchParams.get("artifact");
  const requestedMediaId = searchParams.get("media");

  // -----------------------------------------------------------------
  // Status
  // -----------------------------------------------------------------

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const next = await fetchRetentionEventStatus(eventId);

        if (active) {
          setStatus(next);
        }
      } catch (error) {
        if (!active) {
          return;
        }

        const statusError =
          error instanceof RetentionStatusError
            ? error
            : new RetentionStatusError("Something went wrong.", "failed");

        // A 401 means the session lapsed between the click and the fetch. Bounce to
        // login carrying this page as the destination, so the deep link survives.
        if (statusError.kind === "unauthenticated") {
          navigate(`/login?redirect=${encodeURIComponent(currentPath())}`, {
            replace: true,
          });

          return;
        }

        setLoadError(statusError);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    if (eventId) {
      load();
    }

    return () => {
      active = false;
    };
  }, [eventId, navigate]);

  // -----------------------------------------------------------------
  // Downloads
  // -----------------------------------------------------------------

  const runDownload = useCallback(
    async (id: string, action: () => Promise<void>) => {
      setBusyIds((current) => [...current, id]);

      try {
        await action();
      } catch (error) {
        const message =
          error instanceof RetentionStatusError
            ? error.message
            : "That download could not be completed.";

        setError(message);

        // An expired or restored event invalidates the whole page, not just the button
        // that was pressed, so the status is re-read rather than left stale.
        if (
          error instanceof RetentionStatusError &&
          error.kind === "unavailable"
        ) {
          try {
            setStatus(await fetchRetentionEventStatus(eventId));
          } catch {
            setStatus(null);
            setLoadError(
              new RetentionStatusError(
                "This deletion is no longer available to download.",
                "unavailable",
              ),
            );
          }
        }
      } finally {
        setBusyIds((current) => current.filter((busy) => busy !== id));
      }
    },
    [eventId, setError],
  );

  const downloadArtifact = useCallback(async () => {
    if (!status?.artifact) {
      return;
    }

    const { artifact_id: artifactId, file_name: fileName } = status.artifact;

    await runDownload(artifactId, () =>
      downloadRetentionArtifact(
        eventId,
        artifactId,
        fileName || `${status.root_label ?? "deletion"}-export.xlsx`,
      ),
    );
  }, [eventId, runDownload, status]);

  const downloadMedia = useCallback(
    async (media: RetentionMediaEntry) => {
      await runDownload(media.media_id, () =>
        downloadRetentionMedia(
          eventId,
          media.media_id,
          media.label || media.media_id,
        ),
      );
    },
    [eventId, runDownload],
  );

  // -----------------------------------------------------------------
  // Deep link from the email
  // -----------------------------------------------------------------

  useEffect(() => {
    if (consumedDeepLink || !status || !status.downloads_available) {
      return;
    }

    if (!requestedArtifactId && !requestedMediaId) {
      return;
    }

    // Marked consumed before the await, so a re-render mid-download cannot start a
    // second one. The parameter is then dropped from the URL, which also keeps a
    // browser refresh from re-downloading.
    setConsumedDeepLink(true);

    const clearParam = () => {
      const next = new URLSearchParams(searchParams);
      next.delete("artifact");
      next.delete("media");
      setSearchParams(next, { replace: true });
    };

    if (
      requestedArtifactId &&
      status.artifact?.artifact_id === requestedArtifactId
    ) {
      downloadArtifact().finally(clearParam);

      return;
    }

    const media = status.media.find(
      (entry) => entry.media_id === requestedMediaId,
    );

    if (media && media.download_path) {
      downloadMedia(media).finally(clearParam);

      return;
    }

    // The link named something this event no longer offers — a purged media object, or
    // an artifact replaced by a regenerated one. The list below is still valid, so the
    // page says so instead of failing.
    setError("That file is no longer listed for this deletion.");
    clearParam();
  }, [
    consumedDeepLink,
    downloadArtifact,
    downloadMedia,
    requestedArtifactId,
    requestedMediaId,
    searchParams,
    setError,
    setSearchParams,
    status,
  ]);

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------

  const downloadableMedia = useMemo(
    () => (status?.media ?? []).filter((entry) => entry.download_path !== null),
    [status],
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-96">
          <Spinner size={40} />
        </div>
      </Layout>
    );
  }

  if (loadError || !status) {
    return (
      <Layout>
        <Notice
          title="This download is no longer available"
          body={
            loadError?.message ??
            "This deletion is no longer available to download."
          }
          hint="Retention downloads stop working once the 30-day window closes or the deletion is restored."
          onDashboard={() => navigate("/dashboard")}
        />
      </Layout>
    );
  }

  if (!status.downloads_available) {
    return (
      <Layout>
        <Notice
          title={
            status.restored_at
              ? `${status.root_label ?? "This item"} was restored`
              : "The retention window has closed"
          }
          body={
            status.restored_at
              ? "The deletion was undone, so there is nothing to download. The records are back in MediaBridge."
              : `The retention window for ${status.root_label ?? "this deletion"} ended on ${formatDate(status.retention_deadline)}. The retained media has been permanently removed.`
          }
          onDashboard={() => navigate("/dashboard")}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            Deleted {status.root_type.replace("_", " ")}
          </p>
          <h1 className="text-2xl font-semibold mt-1">
            {status.root_label ?? "Deleted item"}
          </h1>
          <p className="text-sm text-gray-600 mt-2">
            Deleted {formatDate(status.deleted_at)}. Available to download until{" "}
            {formatDate(status.retention_deadline)}.
          </p>
        </header>

        <div className="flex items-center gap-2 rounded-lg bg-white p-4 mb-6 text-sm">
          <Clock size={16} className="text-gray-500" />
          <span>
            {status.remaining_days === null
              ? "Retention window in progress."
              : `${status.remaining_days} ${status.remaining_days === 1 ? "day" : "days"} remaining. After the deadline the retained media is permanently removed.`}
          </span>
        </div>

        <Section title="Export">
          {status.artifact ? (
            <Row
              icon={<FileSpreadsheet size={18} className="text-gray-500" />}
              label={status.artifact.file_name ?? "Deletion export"}
              meta={formatBytes(status.artifact.size_bytes)}
              busy={busyIds.includes(status.artifact.artifact_id)}
              onDownload={downloadArtifact}
            />
          ) : (
            <p className="text-sm text-gray-600">
              {status.export_state === "failed"
                ? "The export could not be generated. Your MediaBridge administrator has been notified."
                : "The export is still being generated. Refresh this page in a moment."}
            </p>
          )}
        </Section>

        <Section title={`Retained media (${downloadableMedia.length})`}>
          {downloadableMedia.length === 0 ? (
            <p className="text-sm text-gray-600">
              No retained media is available for this deletion.
            </p>
          ) : (
            <ul className="divide-y">
              {downloadableMedia.map((media) => (
                <li key={media.media_id}>
                  <Row
                    icon={<Download size={18} className="text-gray-500" />}
                    label={media.label}
                    meta={formatBytes(media.size_bytes)}
                    busy={busyIds.includes(media.media_id)}
                    onDownload={() => downloadMedia(media)}
                  />
                </li>
              ))}
            </ul>
          )}
        </Section>

        {!status.inventory_complete && (
          <p className="flex items-start gap-2 text-sm text-gray-600 mt-4">
            <AlertCircle size={16} className="mt-0.5 text-amber-500" />
            Some media for this deletion could not be catalogued, so this list
            may be incomplete. Nothing has been removed.
          </p>
        )}
      </div>
    </Layout>
  );
}

// -----------------------------------------------------------------
// Presentation
// -----------------------------------------------------------------

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg bg-white p-4 mb-4">
      <h2 className="text-sm font-semibold mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Row({
  icon,
  label,
  meta,
  busy,
  onDownload,
}: {
  icon: React.ReactElement;
  label: string;
  meta: string | null;
  busy: boolean;
  onDownload: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div className="flex items-center gap-3 min-w-0">
        {icon}
        <div className="min-w-0">
          <p className="text-sm truncate" title={label}>
            {label}
          </p>
          {meta && <p className="text-xs text-gray-500">{meta}</p>}
        </div>
      </div>
      <Button
        type="outline"
        text="Download"
        isLoading={busy}
        onClick={onDownload}
      />
    </div>
  );
}

function Notice({
  title,
  body,
  hint,
  onDashboard,
}: {
  title: string;
  body: string;
  hint?: string;
  onDashboard: () => void;
}) {
  return (
    <div className="max-w-xl mx-auto rounded-lg bg-white p-8 text-center">
      <h1 className="text-xl font-semibold">{title}</h1>
      <p className="text-sm text-gray-600 mt-3">{body}</p>
      {hint && <p className="text-xs text-gray-500 mt-2">{hint}</p>}
      <div className="mt-6 flex justify-center">
        <Button type="primary" text="Go to dashboard" onClick={onDashboard} />
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// Formatting
// -----------------------------------------------------------------

/** The current path plus query, for a login round trip that keeps the deep link. */
function currentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function formatDate(value: string | null): string {
  if (!value) {
    return "an unknown date";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "an unknown date";
  }

  return parsed.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBytes(bytes: number | null): string | null {
  if (bytes === null || bytes <= 0) {
    return null;
  }

  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }

  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}
